import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Login from "./components/Login";
import Home from "./components/Home";
import RollUp from "./components/RollUp";
import Dashboard from "./components/Dashboard";
import DailyEntry from "./components/DailyEntry";
import Reports from "./components/Reports";
import SiteManagement from "./components/SiteManagement";
import TelephoneDiscussions from "./components/TelephoneDiscussions";
import ManageLists from "./components/ManageLists";
import SiteManagerMaintenance from "./components/SiteManagerMaintenance";
import CalendarView from "./components/CalendarView";
import TaskManagementPage from "./components/TaskManagementPage";
import Tasks from "./components/Tasks";
import ContractorView from "./components/ContractorView";
import Navbar from "./components/Navbar";
import LookForwardPlanner from "./components/LookForwardPlanner";
import DefectsManagement from "./components/DefectsManagement";
import IncompleteWork from "./components/IncompleteWork";
import FitOffPublic from "./components/FitOffPublic";
import DefectsDetails from "./components/DefectsDetails";
import DefectsList from "./components/DefectsList";
import DefectsReporting from "./components/DefectsReporting";
import ContractorDefects from "./components/ContractorDefects";
import ContractorDefectsPublic from "./components/ContractorDefectsPublic";
import ClientComms from "./components/ClientComms";
import ClientCommsPublic from "./components/ClientCommsPublic";
import ClientVariationPublic from "./components/ClientVariationPublic";
import SitePhotos from "./components/SitePhotos";
import ClientPhotoUpdatePublic from "./components/ClientPhotoUpdatePublic";
import Help from "./components/Help";
import OHSCheckIn from "./components/OHSCheckIn";
import OHSHome from "./components/OHSHome";
import OHSManagement from "./components/OHSManagement";
import ToolboxTalks from "./components/ToolboxTalks";
import ExpenseClaims from "./components/ExpenseClaims";
import Landing from "./components/Landing";
import SiteInductionRecords from "./components/SiteInductionRecords";
import SiteInductionForm from "./components/SiteInductionForm";
import SafetyWalkManagement from "./components/SafetyWalkManagement";
import SafetyWalkForm from "./components/SafetyWalkForm";
import TaskComplete from "./components/TaskComplete";
import DemoSetPassword from "./components/DemoSetPassword";
import ResetPassword from "./components/ResetPassword";
import ClientHandover from "./components/ClientHandover";
import WalkthroughPage from "./components/WalkthroughPage";
import HandoverPublic from "./components/HandoverPublic";
import DemoApproval from "./components/DemoApproval";
import DemoExpired from "./components/DemoExpired";
import DemoCreateSite from "./components/DemoCreateSite";
import DemoAdmin from "./components/DemoAdmin";
import BillingSuccess from "./components/BillingSuccess";
import BillingSettings from "./components/BillingSettings";
import BillingLocked from "./components/BillingLocked";
import PublicPricing from "./components/PublicPricing";
import CompanyProfile from "./components/CompanyProfile";
import AdminCompanies from "./components/AdminCompanies";
import PlatformBranding from "./components/PlatformBranding";
import SupportContact from "./components/SupportContact";
import SubscriptionsAdmin from "./components/SubscriptionsAdmin";
import { TrialRibbon } from "./components/demo/TrialRibbon";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const AuthContext = React.createContext();

// Layout wrapper component to show navbar on all pages
function AppLayout({ children }) {
  const { user } = React.useContext(AuthContext);
  const location = useLocation();
  const isDemo = user?.is_demo && !user?.demo_converted;

  if (isDemo) {
    const expired = user?.demo_expires && new Date(user.demo_expires) < new Date();
    if (expired) return <Navigate to="/demo-expired" replace />;
    const hasSite = (user?.assigned_sites || []).length > 0;
    if (!hasSite && location.pathname !== "/demo/create-site") {
      return <Navigate to="/demo/create-site" replace />;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50">
      <Navbar />
      <main className="pt-16">
        {isDemo && <TrialRibbon user={user} />}
        {children}
      </main>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Verify token and get user info
      fetch(`${API}/auth/me`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      })
      .then(res => {
        if (res.ok) {
          return res.json();
        } else if (res.status === 402) {
          // BILLING_LOCKED — keep token so they can fix payment
          window.location.replace("/billing-locked");
          throw new Error("Billing locked");
        } else {
          localStorage.removeItem("token");
          return res.json().catch(() => ({})).then(d => {
            if (d.detail === "SESSION_EXPIRED") window.location.replace("/login?expired=1");
            throw new Error("Invalid token");
          });
        }
      })
      .then(data => {
        setUser(data);
      })
      .catch(err => {
        console.error("Auth verification failed:", err);
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    console.log("Login function called with:", { token: token?.substring(0, 20) + "...", userData });
    localStorage.setItem("token", token);
    setUser(userData);
    console.log("User state updated, should navigate to dashboard");
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (res.ok) setUser(await res.json());
    } catch (e) {
      console.error("refreshUser failed", e);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading Site Pro App...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/ohs/check-in/:siteId" element={<OHSCheckIn />} />
            <Route path="/induction/:siteId" element={<SiteInductionForm />} />
            <Route path="/task-complete/:taskId/:token" element={<TaskComplete />} />
            <Route path="/contractor-defects/:token" element={<ContractorDefectsPublic />} />
            <Route path="/fitoff/:token" element={<FitOffPublic />} />
            <Route path="/handover/:token" element={<HandoverPublic />} />
            <Route path="/client-comms/:token" element={<ClientCommsPublic />} />
            <Route path="/client-variation/:token" element={<ClientVariationPublic />} />
            <Route path="/client-photo-update/:token" element={<ClientPhotoUpdatePublic />} />
            <Route path="/demo/set-password/:token" element={<DemoSetPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/admin/demo-approval/:token" element={<DemoApproval />} />
            <Route path="/billing-locked" element={<BillingLocked />} />
            <Route path="/pricing" element={<PublicPricing />} />
            
            {!user ? (
              // Not logged in - public marketing landing at root, login at /login
              <>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </>
            ) : (
              // Logged in - show app
              <>
                <Route path="/demo/create-site" element={<DemoCreateSite />} />
                <Route path="/demo-expired" element={<DemoExpired />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/billing-locked" element={<BillingLocked />} />
                <Route path="/billing" element={<AppLayout><BillingSettings /></AppLayout>} />
                <Route path="/admin/demo" element={<AppLayout><DemoAdmin /></AppLayout>} />
                <Route path="/admin/subscriptions" element={<AppLayout><SubscriptionsAdmin /></AppLayout>} />
                <Route path="/" element={<AppLayout><Home /></AppLayout>} />
                <Route path="/dashboard" element={<AppLayout><Dashboard /></AppLayout>} />
                <Route path="/overview" element={<AppLayout><RollUp /></AppLayout>} />
                <Route path="/daily-entry" element={<AppLayout><DailyEntry /></AppLayout>} />
                <Route path="/calendar" element={<AppLayout><CalendarView /></AppLayout>} />
                <Route path="/planner" element={<AppLayout><LookForwardPlanner /></AppLayout>} />
                <Route path="/tasks" element={<AppLayout><Tasks /></AppLayout>} />
                <Route path="/expenses" element={<AppLayout><ExpenseClaims /></AppLayout>} />
                <Route path="/reports" element={<AppLayout><Reports /></AppLayout>} />
                <Route path="/contractor-view" element={<AppLayout><ContractorView /></AppLayout>} />
                <Route path="/phone-records" element={<AppLayout><TelephoneDiscussions /></AppLayout>} />
                <Route path="/site-photos" element={<AppLayout><SitePhotos /></AppLayout>} />
                <Route path="/defects" element={<AppLayout><DefectsManagement /></AppLayout>} />
                <Route path="/defects/contractors" element={<AppLayout><ContractorDefects /></AppLayout>} />
                <Route path="/defects/client-walkthroughs" element={<AppLayout><ClientHandover /></AppLayout>} />
                <Route path="/defects/walkthrough/:walkthroughId" element={<AppLayout><WalkthroughPage /></AppLayout>} />
                <Route path="/work-in-progress" element={<AppLayout><IncompleteWork /></AppLayout>} />
                <Route path="/defects/incomplete-work" element={<Navigate to="/work-in-progress" replace />} />
                <Route path="/defects/details" element={<AppLayout><DefectsDetails /></AppLayout>} />
                <Route path="/defects/:siteId/details" element={<AppLayout><DefectsDetails /></AppLayout>} />
                <Route path="/defects/:siteId/dwelling/:dwellingId" element={<AppLayout><DefectsList /></AppLayout>} />
                <Route path="/defects/reporting" element={<AppLayout><DefectsReporting /></AppLayout>} />
                <Route path="/clients" element={<AppLayout><ClientComms /></AppLayout>} />
                <Route path="/client-comms" element={<AppLayout><ClientComms /></AppLayout>} />
                <Route path="/ohs" element={<AppLayout><OHSHome /></AppLayout>} />
                <Route path="/ohs/attendance" element={<AppLayout><OHSManagement /></AppLayout>} />
                <Route path="/ohs/inductions" element={<AppLayout><SiteInductionRecords /></AppLayout>} />
                <Route path="/ohs/safety-walks" element={<AppLayout><SafetyWalkManagement /></AppLayout>} />
                <Route path="/ohs/safety-walks/new" element={<AppLayout><SafetyWalkForm /></AppLayout>} />
                <Route path="/ohs/safety-walks/:walkId" element={<AppLayout><SafetyWalkForm /></AppLayout>} />
                <Route path="/ohs/safety-walks/:walkId/edit" element={<AppLayout><SafetyWalkForm /></AppLayout>} />
                <Route path="/ohs/toolbox-talks" element={<AppLayout><ToolboxTalks /></AppLayout>} />
                <Route path="/help" element={<AppLayout><Help /></AppLayout>} />
                <Route path="/site-management" element={<AppLayout><SiteManagement /></AppLayout>} />
                <Route path="/sites" element={<AppLayout><SiteManagement /></AppLayout>} />
                <Route path="/site-managers" element={<AppLayout><SiteManagerMaintenance /></AppLayout>} />
                <Route path="/telephone-discussions" element={<AppLayout><TelephoneDiscussions /></AppLayout>} />
                <Route path="/manage-lists" element={<AppLayout><ManageLists /></AppLayout>} />
                <Route path="/company-profile" element={<AppLayout><CompanyProfile /></AppLayout>} />
                <Route path="/admin/branding" element={<AppLayout><PlatformBranding /></AppLayout>} />
                <Route path="/admin/companies" element={<AppLayout><AdminCompanies /></AppLayout>} />
                <Route path="/support" element={<AppLayout><SupportContact /></AppLayout>} />
                <Route path="/login" element={<Navigate to="/" />} />
                <Route path="*" element={<Navigate to="/" />} />
              </>
            )}
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthContext.Provider>
  );
}

export default App;